import { useEffect, useRef, useState } from "react";
import { ArrowDown, Bot, Circle, FileText, Loader2, MessageCircleQuestion, MessagesSquare, Paperclip, Send, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

// Statistiques de l'assistant IA (demande du propriétaire, 2026-10-06) :
// conversations, activité récente, thèmes les plus posés et dernières questions —
// pour savoir ce que les visiteurs cherchent et ajuster le site en conséquence.
// + RÉPONSE HUMAINE (demande du propriétaire, 2026-11-01) : répondre au visiteur
// directement depuis le tableau de bord pendant qu'il est en ligne — sa fenêtre
// de tchat récupère la réponse en 5 s.
export default function ChatbotStats() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [convs, setConvs] = useState([]);
  const [ouvert, setOuvert] = useState("");        // session_id de la conversation ouverte
  const [reponse, setReponse] = useState("");
  const [envoiBusy, setEnvoiBusy] = useState(false);
  const [fichierBusy, setFichierBusy] = useState(0); // % du téléversement d'un fichier joint (0 = rien)
  const fichierRef = useRef(null);
  const listeRef = useRef(null);

  // Joindre un FICHIER à la conversation (demande du propriétaire, 2026-11-01) :
  // même téléversement par morceaux que le visiteur — le cookie personnel fait
  // que le message part côté « atelier » et le visiteur le reçoit au polling
  const envoyerFichier = async (sid, f) => {
    if (!f || fichierBusy) return;
    if (f.size > 80 * 1024 * 1024) { toast.error("Fichier trop lourd (80 Mo max)"); return; }
    setFichierBusy(1);
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    try {
      const r0 = await fetch(`${apiUrl}/api/chat/${sid}/fichier/debut`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ filename: f.name, size: f.size, content_type: f.type }),
      });
      if (!r0.ok) {
        const d = await r0.json().catch(() => ({}));
        toast.error(d.detail || "Type de fichier non accepté");
        return;
      }
      const { upload_id, max_chunk } = await r0.json();
      const total = Math.ceil(f.size / max_chunk);
      for (let i = 0; i < total; i++) {
        const rc = await fetch(`${apiUrl}/api/chat/${sid}/fichier/${upload_id}/chunk?seq=${i}`, {
          method: "PUT", headers: { "Content-Type": "application/octet-stream" }, credentials: "include",
          body: f.slice(i * max_chunk, (i + 1) * max_chunk),
        });
        if (!rc.ok) throw new Error("chunk");
        setFichierBusy(Math.round(((i + 1) / total) * 100));
      }
      const r2 = await fetch(`${apiUrl}/api/chat/${sid}/fichier/${upload_id}/terminer`, {
        method: "POST", credentials: "include",
      });
      if (!r2.ok) throw new Error("terminer");
      toast.success(`Fichier « ${f.name} » envoyé au visiteur.`);
      const r = await api.get("/admin/claude/chatbot/conversations");
      setConvs(r.data.conversations || []);
    } catch {
      toast.error("Le fichier n'est pas parti — réessayez.");
    } finally {
      setFichierBusy(0);
      if (fichierRef.current) fichierRef.current.value = "";
    }
  };

  useEffect(() => {
    let alive = true;
    api.get("/admin/claude/chatbot/stats")
      .then((r) => { if (alive) setD(r.data); })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, []);

  // Conversations en direct — rafraîchies aux 8 s (badge « en ligne » + nouveaux messages)
  useEffect(() => {
    let alive = true;
    const charger = () => api.get("/admin/claude/chatbot/conversations")
      .then((r) => { if (alive) setConvs(r.data.conversations || []); })
      .catch(() => {});
    charger();
    const t = setInterval(charger, 8000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // DÉFILEMENT LIBRE (demande du propriétaire, 2026-11-03) : AUCUN auto-scroll
  // forcé — l'utilisateur garde le contrôle de la molette. Une petite flèche ↓
  // apparaît pour descendre au bas « à son gré ». On détecte juste si on est
  // loin du bas (pour afficher la flèche).
  const [loinDuBas, setLoinDuBas] = useState(false);
  const auDefilement = () => {
    const el = listeRef.current;
    if (!el) return;
    setLoinDuBas(el.scrollTop + el.clientHeight < el.scrollHeight - 48);
  };
  const descendreEnBas = () => {
    const el = listeRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  const repondre = async (sid) => {
    const texte = reponse.trim();
    if (!texte || envoiBusy) return;
    setEnvoiBusy(true);
    try {
      const { data } = await api.post(`/admin/claude/chatbot/${sid}/repondre`, { message: texte });
      setReponse("");
      toast.success(data.courriel_envoye
        ? "Réponse envoyée — le visiteur n'était plus en ligne : il l'a aussi reçue PAR COURRIEL avec le lien du clavardage."
        : "Réponse envoyée — elle apparaît chez le visiteur en quelques secondes.");
      const r = await api.get("/admin/claude/chatbot/conversations");
      setConvs(r.data.conversations || []);
    } catch (e2) {
      toast.error("La réponse n'est pas partie — réessayez.");
    } finally {
      setEnvoiBusy(false);
    }
  };

  return (
    <div data-testid="chatbot-stats">
      <p className="font-semibold flex items-center gap-2 mb-1">
        <Bot className="w-4 h-4 text-gold" /> Clavardage en direct
      </p>
      <p className="text-xs text-ivory/70 mb-6">
        Ce que les visiteurs demandent au clavardage du site — utile pour repérer les questions sans réponse facile.
      </p>

      {/* RÉPONSE HUMAINE — conversations en direct (demande du propriétaire, 2026-11-01) */}
      {convs.length > 0 && (
        <div className="mb-8">
          <p className="text-[10px] uppercase tracking-widest text-gold mb-3">
            Conversations du tchat — répondre en personne
          </p>
          <div className="space-y-2" data-testid="chatbot-conversations">
            {convs.map((c) => (
              <div key={c.session_id} className="bg-surface border border-ivory/10 rounded-xl overflow-hidden"
                data-testid={`chatbot-conv-${c.session_id.slice(0, 12)}`}>
                <button type="button" onClick={() => setOuvert(ouvert === c.session_id ? "" : c.session_id)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-ink/40 transition-colors">
                  <Circle className={`w-2.5 h-2.5 shrink-0 ${c.en_ligne ? "fill-emerald-400 text-emerald-400" : "fill-ivory/20 text-ivory/20"}`} />
                  <span className="flex-1 min-w-0">
                    {(c.visiteur_nom || c.visiteur_courriel) && (
                      <span className="block text-[11px] font-semibold text-gold truncate"
                        data-testid={`chatbot-visiteur-${c.session_id.slice(0, 12)}`}>
                        {c.visiteur_nom || ""}{c.visiteur_nom && c.visiteur_courriel ? " — " : ""}{c.visiteur_courriel || ""}
                      </span>
                    )}
                    <span className="block text-xs text-ivory/85 truncate">« {c.apercu || "…"} »</span>
                    <span className="block text-[10px] text-ivory/40">
                      {(c.updated_at || "").slice(0, 16).replace("T", " ")} · {c.nb_messages} message{c.nb_messages > 1 ? "s" : ""}
                    </span>
                  </span>
                  {c.en_ligne && (
                    <span className="text-[10px] font-bold text-emerald-300 bg-emerald-500/15 border border-emerald-400/30 rounded-full px-2.5 py-1 shrink-0"
                      data-testid={`chatbot-enligne-${c.session_id.slice(0, 12)}`}>
                      EN LIGNE
                    </span>
                  )}
                </button>
                {ouvert === c.session_id && (
                  <div className="border-t border-ivory/10 px-4 py-3">
                    {/* Zone de messages : défilement LIBRE à la molette (aucun
                        auto-scroll forcé) + flèche ↓ flottante pour descendre au
                        bas « à son gré » (demande du propriétaire, 2026-11-03) */}
                    <div className="relative">
                      <div ref={listeRef} onScroll={auDefilement} data-lenis-prevent
                        className="max-h-64 min-h-0 overflow-y-auto overscroll-contain space-y-2 mb-3 pr-1"
                        data-testid="chatbot-conv-messages">
                      {c.messages.filter((m) => m.role !== "system").map((m, i) => (
                        <div key={i} className={`text-xs rounded-lg px-3 py-2 max-w-[85%] ${
                          m.role === "user"
                            ? "bg-gold/15 border border-gold/25 text-ivory/90"
                            : m.auteur === "atelier"
                              ? "bg-emerald-500/15 border border-emerald-400/30 text-emerald-100 ml-auto"
                              : "bg-ink border border-ivory/10 text-ivory/60"}`}>
                          {m.auteur === "atelier" && (
                            <span className="block text-[9px] font-bold uppercase tracking-widest text-emerald-300 mb-0.5">Vous — en personne</span>
                          )}
                          {m.fichier && (
                            m.fichier.image
                              ? <a href={`${process.env.REACT_APP_BACKEND_URL}${m.fichier.url}`} target="_blank" rel="noreferrer"
                                  data-testid="chatbot-fichier-image">
                                  <img src={`${process.env.REACT_APP_BACKEND_URL}${m.fichier.url}`} alt={m.fichier.nom}
                                    className="rounded max-h-28 object-contain bg-white/5 mb-1" />
                                  <span className="block text-[10px] opacity-70">{m.fichier.nom}</span>
                                </a>
                              : <a href={`${process.env.REACT_APP_BACKEND_URL}${m.fichier.url}`} target="_blank" rel="noreferrer"
                                  className="flex items-center gap-1.5 underline underline-offset-2 hover:text-gold"
                                  data-testid="chatbot-fichier-lien">
                                  <FileText className="w-3.5 h-3.5 shrink-0" />
                                  {m.fichier.nom} ({(m.fichier.taille / 1024 / 1024).toFixed(1)} Mo)
                                </a>
                          )}
                          {m.texte}
                        </div>
                      ))}
                      </div>
                      {/* Petite flèche ↓ pour descendre au bas à votre gré (visible
                          seulement quand vous n'êtes pas déjà près du bas) */}
                      {loinDuBas && (
                        <button type="button" onClick={descendreEnBas}
                          data-testid="chatbot-scroll-bas" title="Aller au plus récent"
                          className="absolute bottom-4 right-3 z-10 bg-gold text-ink rounded-full p-2 shadow-lg hover:bg-champagne transition-colors">
                          <ArrowDown className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="file" ref={fichierRef} className="hidden" data-testid="chatbot-fichier-input"
                        accept=".png,.jpg,.jpeg,.webp,.gif,.heic,.pdf,.ai,.eps,.svg,.psd,.zip,.doc,.docx,.txt,.csv,.xlsx"
                        onChange={(e) => envoyerFichier(c.session_id, e.target.files?.[0])} />
                      <button onClick={() => fichierRef.current?.click()} disabled={!!fichierBusy}
                        data-testid="chatbot-fichier-btn" title="Joindre un fichier (80 Mo max)"
                        className="text-ivory/60 hover:text-gold rounded-full p-2 border border-ivory/15 hover:border-gold/50 transition-colors disabled:opacity-50">
                        {fichierBusy ? <span className="text-[10px] font-bold text-gold">{fichierBusy}%</span> : <Paperclip className="w-3.5 h-3.5" />}
                      </button>
                      <input value={reponse} onChange={(e) => setReponse(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") repondre(c.session_id); }}
                        placeholder="Votre réponse au visiteur…"
                        maxLength={1000} data-testid="chatbot-reponse-input"
                        className="flex-1 bg-ink border border-ivory/15 rounded-full px-4 py-2 text-xs focus:border-emerald-400 focus:outline-none transition-colors" />
                      <button onClick={() => repondre(c.session_id)} disabled={envoiBusy || !reponse.trim()}
                        data-testid="chatbot-repondre-btn"
                        className="inline-flex items-center gap-1.5 bg-emerald-500 text-ink font-bold text-xs px-4 py-2 rounded-full hover:bg-emerald-400 transition-colors disabled:opacity-50">
                        {envoiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        Répondre
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {err ? (
        <p className="text-sm text-red-300" data-testid="chatbot-stats-error">Statistiques indisponibles — réessayez.</p>
      ) : !d ? (
        <p className="text-sm text-ivory/50" data-testid="chatbot-stats-loading">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Chargement…
        </p>
      ) : d.conversations === 0 ? (
        <p className="text-sm text-ivory/50" data-testid="chatbot-stats-empty">
          Aucune conversation pour l'instant — l'assistant répondra aux visiteurs via la bulle de clavardage du site.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {[
              [MessagesSquare, "Conversations", d.conversations, "text-gold", "conversations"],
              [MessageCircleQuestion, "Questions posées", d.messages, "text-ivory", "messages"],
              [TrendingUp, "Aujourd'hui", d.aujourd_hui, "text-emerald-300", "aujourdhui"],
              [TrendingUp, "7 derniers jours", d.sept_jours, "text-champagne", "semaine"],
            ].map(([Icon, lab, val, cls, tid]) => (
              <div key={tid} className="bg-surface border border-ivory/10 rounded-xl px-4 py-3">
                <p className="text-[10px] uppercase tracking-widest text-ivory/50 flex items-center gap-1.5">
                  <Icon className="w-3 h-3" /> {lab}
                </p>
                <p className={`font-serif text-2xl mt-1 ${cls}`} data-testid={`chatbot-stat-${tid}`}>{val}</p>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gold mb-3">Thèmes les plus demandés</p>
              <ul className="space-y-2" data-testid="chatbot-themes">
                {d.themes.map((t) => {
                  const max = d.themes[0]?.nb || 1;
                  return (
                    <li key={t.theme} className="text-xs" data-testid={`chatbot-theme-${t.theme}`}>
                      <div className="flex justify-between mb-1">
                        <span className="text-ivory/80">{t.label}</span>
                        <span className="text-gold font-semibold">{t.nb}</span>
                      </div>
                      <div className="h-1.5 bg-ink rounded-full overflow-hidden border border-ivory/10">
                        <div className="h-full bg-gold/80 rounded-full" style={{ width: `${Math.max(4, Math.round((t.nb / max) * 100))}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gold mb-3">Dernières questions posées</p>
              <ul className="space-y-2.5" data-testid="chatbot-questions">
                {d.questions.map((q, i) => (
                  <li key={i} className="text-xs bg-surface border border-ivory/10 rounded-lg px-3 py-2.5"
                    data-testid={`chatbot-question-${i}`}>
                    <span className="text-ivory/85 block">« {q.question} »</span>
                    <span className="text-[10px] text-ivory/40">{q.date} · {q.nb_messages} message{q.nb_messages > 1 ? "s" : ""}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
